<template>
  <div class="crest-book-list-form">
    <b-overlay :show="pageLoading || saving" rounded="sm">
      <div class="crest-page-header">
        <b-button
          variant="outline-secondary"
          class="btn-icon rounded-circle crest-back-btn"
          @click="goBack"
        >
          <feather-icon icon="ArrowLeftIcon" />
        </b-button>
        <div class="crest-page-header__icon">
          <feather-icon icon="BookOpenIcon" size="22" />
        </div>
        <div class="flex-grow-1">
          <h3 class="crest-page-title">{{ pageTitle }}</h3>
          <p class="crest-page-subtitle">
            Add books by subject and stationery in one list.
          </p>
        </div>
      </div>

      <b-card class="crest-form-card">
        <section class="crest-section">
          <div class="crest-section-heading crest-section-heading--simple">
            <div class="crest-section-heading__content">
              <span class="crest-step">1</span>
              <div>
                <h5 class="crest-section-title mb-25">Class &amp; Section</h5>
                <small class="text-muted">
                  Choose where this book list will be used.
                </small>
              </div>
            </div>
          </div>
          <b-row v-if="isView">
            <b-col md="6" class="mb-1 mb-md-0">
              <span class="crest-label">Class</span>
              <div class="crest-value">{{ getClassName(form.classID) }}</div>
            </b-col>
            <b-col md="6">
              <span class="crest-label">Section</span>
              <div class="crest-value">
                {{ getSectionName(form.classID, form.sectionID) }}
              </div>
            </b-col>
          </b-row>
          <b-row v-else>
            <b-col md="6" class="mb-1 mb-md-0">
              <b-form-group label="Class *" class="mb-0">
                <v-select
                  v-model="form.classID"
                  :options="classes"
                  label="name"
                  :reduce="(item) => item.id"
                  :clearable="false"
                  placeholder="Select class"
                  append-to-body
                  @input="onClassChange"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Section" class="mb-0">
                <v-select
                  v-model="form.sectionID"
                  :options="sections"
                  label="section"
                  :reduce="(item) => item.id"
                  :clearable="false"
                  :disabled="!form.classID"
                  placeholder="Select section"
                  append-to-body
                  @input="onSectionChange"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </section>

        <section class="crest-section">
          <div class="crest-section-heading">
            <div class="crest-section-heading__content">
              <span class="crest-step">2</span>
              <div>
                <h5 class="crest-section-title mb-25">Books by Subject</h5>
                <small class="text-muted">
                  Add a subject first, then list all required books.
                </small>
              </div>
            </div>
            <div v-if="!isView" class="crest-subject-adder">
              <v-select
                v-model="subjectToAdd"
                :options="availableSubjects"
                label="name"
                :reduce="(item) => item.id"
                :disabled="!form.classID || subjectsLoading"
                :placeholder="
                  subjectsLoading ? 'Loading subjects...' : 'Select subject'
                "
                append-to-body
                class="crest-subject-select"
              />
              <b-button
                variant="primary"
                class="ml-50"
                :disabled="!subjectToAdd"
                @click="addSubject"
              >
                <feather-icon icon="PlusIcon" size="16" class="mr-25" />
                Add Subject
              </b-button>
            </div>
          </div>

          <div v-if="!subjectGroups.length" class="crest-empty">
            <feather-icon icon="BookOpenIcon" size="28" />
            <span>No subjects added yet.</span>
          </div>

          <div
            v-for="(group, subjectIndex) in subjectGroups"
            :key="'subject-' + group.subjectID"
            class="crest-subject-card"
          >
            <div class="crest-subject-head">
              <div class="d-flex align-items-center">
                <span class="crest-subject-number">{{ subjectIndex + 1 }}</span>
                <div>
                  <strong class="crest-subject-name">
                    {{ getSubjectName(group.subjectID) }}
                  </strong>
                  <small class="crest-book-count">
                    {{ group.books.length }}
                    {{ group.books.length === 1 ? "book" : "books" }}
                  </small>
                </div>
              </div>
              <b-button
                v-if="!isView"
                variant="flat-danger"
                class="btn-icon"
                v-b-tooltip.hover
                title="Remove subject"
                @click="removeSubject(subjectIndex)"
              >
                <feather-icon icon="Trash2Icon" size="17" />
              </b-button>
            </div>

            <div class="crest-table-wrap">
              <table class="crest-table">
                <thead>
                  <tr>
                    <th>Publisher</th>
                    <th>Book / Particular</th>
                    <th>Copies Required</th>
                    <th class="crest-qty-column">Qty</th>
                    <th v-if="!isView" class="crest-action-column"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(book, bookIndex) in group.books"
                    :key="'book-' + subjectIndex + '-' + bookIndex"
                  >
                    <template v-if="isView">
                      <td>{{ getPublisherName(book.categoryID) }}</td>
                      <td>{{ book.particular || "—" }}</td>
                      <td>{{ book.copiesRequired || "—" }}</td>
                      <td class="text-center">{{ book.qty }}</td>
                    </template>
                    <template v-else>
                      <td>
                        <v-select
                          v-model="book.categoryID"
                          :options="publishers"
                          label="name"
                          :reduce="(item) => item.id"
                          :clearable="false"
                          placeholder="Select publisher"
                          append-to-body
                        />
                      </td>
                      <td>
                        <b-form-input
                          v-model.trim="book.particular"
                          placeholder="Enter book title / particular"
                        />
                      </td>
                      <td>
                        <b-form-input
                          v-model.trim="book.copiesRequired"
                          placeholder="e.g. Four Line Notebook (200 pages)"
                        />
                      </td>
                      <td>
                        <b-form-input
                          v-model.number="book.qty"
                          type="number"
                          min="0"
                          class="text-center"
                        />
                      </td>
                      <td class="text-center">
                        <b-button
                          variant="flat-danger"
                          class="btn-icon"
                          @click="removeBook(subjectIndex, bookIndex)"
                        >
                          <feather-icon icon="XIcon" size="16" />
                        </b-button>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>

            <b-button
              v-if="!isView"
              variant="outline-primary"
              size="sm"
              class="mt-1"
              @click="addBook(subjectIndex)"
            >
              <feather-icon icon="PlusIcon" size="14" class="mr-25" />
              Add Book
            </b-button>
          </div>
        </section>

        <section class="crest-section mb-0">
          <div class="crest-section-heading">
            <div class="crest-section-heading__content">
              <span class="crest-step">3</span>
              <div>
                <h5 class="crest-section-title mb-25">Stationery</h5>
                <small class="text-muted">
                  Add stationery and classroom materials required for the class.
                </small>
              </div>
            </div>
            <b-button
              v-if="!isView"
              variant="outline-primary"
              size="sm"
              @click="addStationary"
            >
              <feather-icon icon="PlusIcon" size="14" class="mr-25" />
              Add Stationery
            </b-button>
          </div>

          <div v-if="!stationary.length" class="crest-empty">
            <feather-icon icon="Edit3Icon" size="28" />
            <span>No stationery added yet.</span>
          </div>

          <div v-else class="crest-table-wrap">
            <table class="crest-table crest-stationary-table">
              <thead>
                <tr>
                  <th>Stationery Item</th>
                  <th class="crest-qty-column">Qty</th>
                  <th v-if="!isView" class="crest-action-column"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in stationary" :key="'stationary-' + index">
                  <template v-if="isView">
                    <td>{{ item.name || "—" }}</td>
                    <td class="text-center">{{ item.qty }}</td>
                  </template>
                  <template v-else>
                    <td>
                      <b-form-input
                        v-model.trim="item.name"
                        placeholder="Enter stationery item"
                      />
                    </td>
                    <td>
                      <b-form-input
                        v-model.number="item.qty"
                        type="number"
                        min="0"
                        class="text-center"
                      />
                    </td>
                    <td class="text-center">
                      <b-button
                        variant="flat-danger"
                        class="btn-icon"
                        @click="removeStationary(index)"
                      >
                        <feather-icon icon="XIcon" size="16" />
                      </b-button>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <div class="crest-form-footer">
          <b-button variant="outline-secondary" @click="goBack">
            {{ isView ? "Back" : "Cancel" }}
          </b-button>
          <b-button
            v-if="!isView"
            variant="primary"
            :disabled="saving"
            @click="saveBookList"
          >
            <b-spinner v-if="saving" small class="mr-50" />
            <feather-icon v-else icon="SaveIcon" size="16" class="mr-50" />
            {{ isEdit ? "Update" : "Save" }}
          </b-button>
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BOverlay,
  BSpinner,
  BCard,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormInput,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import { HEAD_OFFICE_CAMPUS_ID } from "./bookListUtils";

export default {
  name: "BookListCrestViewForm",
  components: {
    BOverlay,
    BSpinner,
    BCard,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      rights: {},
      pageLoading: false,
      saving: false,
      subjectsLoading: false,
      classesData: [],
      classes: [],
      sections: [],
      subjects: [],
      publishers: [],
      subjectToAdd: null,
      form: {
        id: 0,
        classID: null,
        sectionID: 0,
      },
      subjectGroups: [],
      stationary: [],
    };
  },
  computed: {
    isEdit() {
      return this.$route.name === "book-list-crestview-edit";
    },
    isView() {
      return this.$route.name === "book-list-crestview-view";
    },
    pageTitle() {
      if (this.isView) return "View Book List";
      return this.isEdit ? "Edit Book List" : "Add Book List";
    },
    userID() {
      return (
        this.$store.state.userData.userID ||
        this.$store.state.userData.localID ||
        this.$store.state.userData.userLinkID ||
        0
      );
    },
    availableSubjects() {
      const selected = new Set(
        this.subjectGroups.map((group) => Number(group.subjectID))
      );
      return this.subjects.filter((subject) => !selected.has(Number(subject.id)));
    },
  },
  async created() {
    this.loadRights();
    if (!this.hasRouteRight()) return;

    this.pageLoading = true;
    try {
      await Promise.all([this.loadClasses(), this.loadPublishers()]);
      if (this.isEdit || this.isView) {
        await this.loadExistingRecord();
      }
    } finally {
      this.pageLoading = false;
    }
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),

    loadRights() {
      let mapped = {};
      (this.$store.state.menu || []).forEach((menu) => {
        (menu.children || []).forEach((child) => {
          mapped[child.route] = {
            view: child.view,
            add: child.add,
            edit: child.edit,
            delete: child.delete,
          };
        });
      });
      this.$store.commit("setRights", mapped);
      this.rights = this.$store.state.rights["book-list"] || {};
    },
    hasRouteRight() {
      const allowed = this.isView
        ? this.rights.view
        : this.isEdit
        ? this.rights.edit
        : this.rights.add;
      if (!allowed) {
        this.$router.replace({ name: "misc-not-authorized" });
      }
      return allowed;
    },
    apiBase() {
      return this.$store.state.domain;
    },
    dbParam() {
      return encodeURIComponent(this.$store.state.userData.db);
    },
    unwrap(response) {
      let value = response;
      while (
        value &&
        !Array.isArray(value) &&
        typeof value === "object" &&
        value.data !== undefined &&
        Object.keys(value).length <= 3
      ) {
        value = value.data;
      }
      return value;
    },
    normalizeList(response) {
      const value = this.unwrap(response);
      if (Array.isArray(value)) return value;
      if (!value || typeof value !== "object") return [];
      return value.items || value.records || value.data || [];
    },
    normalizeSubject(subject) {
      return {
        ...subject,
        id: subject.id || subject.subjectID || subject.subjectId,
        name:
          subject.name ||
          subject.subject ||
          subject.subjectName ||
          subject.meta_subject ||
          `Subject ${subject.id || subject.subjectID}`,
      };
    },
    getClassName(id) {
      const found = this.classes.find((item) => Number(item.id) === Number(id));
      return found ? found.name : "—";
    },
    getSectionName(classID, sectionID) {
      if (Number(sectionID) === 0) return "All Sections";
      const classData = this.classesData.find(
        (item) => Number(item.cls && item.cls.id) === Number(classID)
      );
      const found = ((classData && classData.sections) || []).find(
        (item) => Number(item.id) === Number(sectionID)
      );
      return found ? found.section || found.name : "—";
    },
    getSubjectName(id) {
      const found = this.subjects.find((item) => Number(item.id) === Number(id));
      const group = this.subjectGroups.find(
        (item) => Number(item.subjectID) === Number(id)
      );
      return found ? found.name : (group && group.subjectName) || `Subject ${id}`;
    },
    getPublisherName(id) {
      const found = this.publishers.find(
        (item) => Number(item.id) === Number(id)
      );
      return found ? found.name : "—";
    },
    buildSections(classID) {
      const classData = this.classesData.find(
        (item) => Number(item.cls && item.cls.id) === Number(classID)
      );
      const list = ((classData && classData.sections) || []).map((section) => ({
        id: section.id,
        section: section.section || section.name || `Section ${section.id}`,
      }));
      return [{ id: 0, section: "All Sections" }, ...list];
    },
    createBook() {
      return {
        id: 0,
        categoryID: null,
        particular: "",
        copiesRequired: "",
        qty: 1,
      };
    },
    createStationary() {
      return { id: 0, name: "", qty: 1 };
    },
    async loadClasses() {
      const response = await this.get({
        url:
          this.apiBase() +
          "myclasses/LoadClasses?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          HEAD_OFFICE_CAMPUS_ID +
          "&dID=0",
        token: this.$store.state.userData.token,
      });
      const list = this.normalizeList(response);
      this.classesData = list;
      this.classes = list
        .map((item) => item.cls)
        .filter((item) => item && item.id);
    },
    async loadPublishers() {
      const response = await this.get({
        url:
          this.apiBase() +
          "BookListCategories/GetByPagination?db=" +
          this.dbParam() +
          "&campusID=" +
          HEAD_OFFICE_CAMPUS_ID +
          "&pageNumber=1&pageSize=1000",
        token: this.$store.state.userData.token,
      });
      this.publishers = this.normalizeList(response).filter(
        (item) => item.isActive !== false
      );
    },
    async loadSubjects() {
      if (!this.form.classID) {
        this.subjects = [];
        return;
      }
      this.subjectsLoading = true;
      try {
        const response = await this.get({
          url:
            this.apiBase() +
            "Marks/LoadClassSubjects?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            HEAD_OFFICE_CAMPUS_ID +
            "&clsID=" +
            this.form.classID +
            "&sec=" +
            Number(this.form.sectionID || 0),
          token: this.$store.state.userData.token,
        });
        let list = this.normalizeList(response);
        if (!list.length) {
          const fallback = await this.get({
            url:
              this.apiBase() +
              "Subjects?db=" +
              this.$store.state.userData.db +
              "&cId=" +
              HEAD_OFFICE_CAMPUS_ID,
            token: this.$store.state.userData.token,
          });
          list = this.normalizeList(fallback);
        }
        const existing = this.subjectGroups.map((group) => ({
          id: group.subjectID,
          name: group.subjectName,
        }));
        const combined = [...list.map(this.normalizeSubject), ...existing];
        this.subjects = combined.filter(
          (item, index, all) =>
            item.id &&
            all.findIndex((other) => Number(other.id) === Number(item.id)) ===
              index
        );
      } catch (error) {
        console.error("Error loading class subjects:", error);
        this.subjects = [];
      } finally {
        this.subjectsLoading = false;
      }
    },
    async onClassChange() {
      this.form.sectionID = 0;
      this.sections = this.buildSections(this.form.classID);
      this.subjectGroups = [];
      this.subjectToAdd = null;
      await this.loadSubjects();
    },
    async onSectionChange() {
      this.subjectGroups = [];
      this.subjectToAdd = null;
      await this.loadSubjects();
    },
    addSubject() {
      if (!this.subjectToAdd) return;
      const subject = this.subjects.find(
        (item) => Number(item.id) === Number(this.subjectToAdd)
      );
      this.subjectGroups.push({
        subjectID: Number(this.subjectToAdd),
        subjectName: subject ? subject.name : "",
        books: [this.createBook()],
      });
      this.subjectToAdd = null;
    },
    removeSubject(index) {
      this.subjectGroups.splice(index, 1);
    },
    addBook(subjectIndex) {
      this.subjectGroups[subjectIndex].books.push(this.createBook());
    },
    removeBook(subjectIndex, bookIndex) {
      const books = this.subjectGroups[subjectIndex].books;
      books.splice(bookIndex, 1);
      if (!books.length) books.push(this.createBook());
    },
    addStationary() {
      this.stationary.push(this.createStationary());
    },
    removeStationary(index) {
      this.stationary.splice(index, 1);
    },
    normalizeBook(book) {
      return {
        id: book.id || 0,
        categoryID: Number(
          book.categoryID || book.categoryId || book.publisherID || 0
        ),
        particular: book.particular || book.name || "",
        copiesRequired:
          book.copiesRequired || book.copies_required || book.notebook || "",
        qty: Number(book.qty != null ? book.qty : 1),
      };
    },
    parseRecord(response) {
      const payload = this.unwrap(response) || {};
      const header = payload.header || payload.record || payload;
      const groups = payload.groups || header.groups || {};
      const rawSubjects =
        groups.subjects ||
        payload.subjects ||
        header.subjects ||
        payload.bookSubjects ||
        [];
      const rawStationary =
        groups.stationary ||
        groups.stationery ||
        payload.stationary ||
        payload.stationery ||
        header.stationary ||
        header.stationery ||
        [];

      return {
        header: {
          id: header.id || this.id || this.$route.params.id || 0,
          classID: header.classID || header.classId || null,
          sectionID:
            header.sectionID != null
              ? header.sectionID
              : header.sectionId != null
              ? header.sectionId
              : 0,
        },
        subjects: (Array.isArray(rawSubjects) ? rawSubjects : []).map(
          (subject) => ({
            subjectID: Number(
              subject.subjectID || subject.subjectId || subject.id || 0
            ),
            subjectName:
              String(
                subject.subjectName ||
              subject.meta_subject ||
              subject.name ||
                  ""
              ).trim(),
            books: (
              subject.books ||
              subject.items ||
              subject.bookListItems ||
              []
            ).map(this.normalizeBook),
          })
        ),
        stationary: (Array.isArray(rawStationary) ? rawStationary : []).map(
          (item) => ({
            id: item.id || 0,
            name: item.name || item.particular || "",
            qty: Number(item.qty != null ? item.qty : 0),
          })
        ),
      };
    },
    async loadExistingRecord() {
      const recordID = this.id || this.$route.params.id;
      const response = await this.get({
        url:
          this.apiBase() +
          "BookList/LoadSelected?db=" +
          this.dbParam() +
          "&campusID=" +
          HEAD_OFFICE_CAMPUS_ID +
          "&id=" +
          recordID,
        token: this.$store.state.userData.token,
      });
      const parsed = this.parseRecord(response);
      this.form = parsed.header;
      this.subjectGroups = parsed.subjects;
      this.stationary = parsed.stationary;
      this.sections = this.buildSections(this.form.classID);
      await this.loadSubjects();
    },
    buildPayload() {
      return {
        header: {
          id: Number(this.form.id || 0),
          classID: Number(this.form.classID),
          sectionID: Number(this.form.sectionID || 0),
        },
        subjects: this.subjectGroups
          .map((group) => ({
            subjectID: Number(group.subjectID),
            books: group.books
              .filter(
                (book) =>
                  Number(book.qty) > 0 ||
                  String(book.particular || "").trim() ||
                  Number(book.categoryID) > 0
              )
              .map((book) => ({
                id: Number(book.id || 0),
                categoryID: Number(book.categoryID || 0),
                particular: String(book.particular || "").trim(),
                copiesRequired: String(book.copiesRequired || "").trim(),
                qty: Number(book.qty || 0),
              })),
          }))
          .filter((group) => group.books.length),
        stationary: this.stationary
          .filter((item) => String(item.name || "").trim())
          .map((item) => ({
            id: Number(item.id || 0),
            name: String(item.name).trim(),
            qty: Number(item.qty || 0),
          })),
      };
    },
    validatePayload(payload) {
      if (!payload.header.classID) {
        return "Please select a class.";
      }
      if (!payload.subjects.length && !payload.stationary.length) {
        return "Please add at least one book or stationery item.";
      }
      const invalidBook = payload.subjects.some((group) =>
        group.books.some(
          (book) =>
            !book.categoryID || !book.particular || Number(book.qty) <= 0
        )
      );
      if (invalidBook) {
        return "Select a publisher, enter the book particular, and set quantity above 0 for every book.";
      }
      return "";
    },
    async saveBookList() {
      const payload = this.buildPayload();
      const error = this.validatePayload(payload);
      if (error) {
        this.$bvToast.toast(error, {
          title: "Please check the form",
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.saving = true;
      try {
        const response = await this.post({
          url:
            this.apiBase() +
            "BookList/Save?db=" +
            this.dbParam() +
            "&campusID=" +
            HEAD_OFFICE_CAMPUS_ID +
            "&userID=" +
            this.userID,
          body: payload,
          token: this.$store.state.userData.token,
          context: this,
          message: this.isEdit
            ? "Book list updated successfully."
            : "Book list saved successfully.",
        });
        if (response !== false && response !== null) this.goBack();
      } catch (error) {
        console.error("Error saving Crest View book list:", error);
      } finally {
        this.saving = false;
      }
    },
    goBack() {
      this.$router.push({ name: "book-list" });
    },
  },
};
</script>

<style lang="scss" scoped>
.crest-book-list-form {
  max-width: 1440px;
  margin: 0 auto;

  .crest-form-card {
    border: 0;
    border-radius: 14px;
    box-shadow: 0 8px 30px rgba(34, 41, 47, 0.08);

    ::v-deep .card-body {
      padding: 1.5rem;
    }
  }
}

.crest-page-header {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1.1rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid #e8e5f5;
  border-radius: 14px;
  background: linear-gradient(135deg, #ffffff 0%, #f7f5ff 100%);
  box-shadow: 0 4px 16px rgba(115, 103, 240, 0.06);
}

.crest-back-btn {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
}

.crest-page-header__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  border-radius: 12px;
  color: #fff;
  background: linear-gradient(135deg, #7367f0, #9e95f5);
  box-shadow: 0 6px 14px rgba(115, 103, 240, 0.24);
}

.crest-page-title {
  margin: 0 0 0.2rem;
  color: #35354f;
  font-size: 1.35rem;
  font-weight: 700;
}

.crest-page-subtitle {
  margin: 0;
  color: #82868b;
  font-size: 0.875rem;
}

.crest-section {
  position: relative;
  padding: 1.4rem;
  margin-bottom: 1.4rem;
  overflow: visible;
  border: 1px solid #e7e5ef;
  border-radius: 12px;
  background: linear-gradient(180deg, #fff 0%, #fdfcff 100%);
}

.crest-section-title {
  color: #35354f;
  font-weight: 700;
  margin-bottom: 1rem;
}

.crest-section-heading,
.crest-subject-head,
.crest-form-footer,
.crest-subject-adder {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.crest-section-heading {
  gap: 1.25rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eeecf4;
}

.crest-section-heading--simple {
  justify-content: flex-start;
}

.crest-section-heading__content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.crest-step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 10px;
  color: #fff;
  background: linear-gradient(135deg, #7367f0, #9e95f5);
  box-shadow: 0 4px 10px rgba(115, 103, 240, 0.22);
  font-size: 0.9rem;
  font-weight: 700;
}

.crest-subject-adder {
  justify-content: flex-end;
  padding: 0.35rem;
  border: 1px solid #e2dff1;
  border-radius: 9px;
  background: #f9f8fe;

  .btn {
    min-height: 40px;
    border-radius: 7px;
    font-weight: 600;
  }
}

.crest-subject-select {
  width: 280px;
}

.crest-subject-card {
  position: relative;
  overflow: hidden;
  border: 1px solid #dedaf2;
  border-radius: 12px;
  margin-bottom: 1.1rem;
  background: #fff;
  box-shadow: 0 4px 14px rgba(76, 67, 139, 0.06);

  > .btn {
    margin: 0 1rem 1rem;
    border-radius: 7px;
    font-weight: 600;
  }

  &::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    content: "";
    background: linear-gradient(180deg, #7367f0, #a89ff6);
  }
}

.crest-subject-head {
  padding: 0.9rem 1rem 0.9rem 1.15rem;
  color: #4c43a5;
  border-bottom: 1px solid #ebe9f3;
  background: linear-gradient(90deg, #f6f4ff 0%, #fbfaff 100%);
}

.crest-subject-name {
  display: block;
  color: #3c3767;
  font-size: 1rem;
}

.crest-book-count {
  display: block;
  margin-top: 0.1rem;
  color: #9894aa;
}

.crest-subject-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-right: 0.65rem;
  border-radius: 9px;
  color: white;
  background: linear-gradient(135deg, #7367f0, #9288f4);
  font-size: 0.8rem;
  font-weight: 700;
}

.crest-table-wrap {
  width: 100%;
  overflow-x: auto;
  padding: 0.7rem 0.75rem 0.25rem;
}

.crest-table {
  width: 100%;
  min-width: 900px;
  table-layout: fixed;
  border-collapse: collapse;

  th {
    padding: 0.55rem 0.6rem;
    color: #77738a;
    border-bottom: 1px solid #e9e7ef;
    background: #faf9fc;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;

    &:first-child {
      width: 20%;
      border-radius: 7px 0 0 7px;
    }

    &:nth-child(2) {
      width: 31%;
    }

    &:nth-child(3) {
      width: 36%;
    }

    &:last-child {
      border-radius: 0 7px 7px 0;
    }
  }

  td {
    padding: 0.55rem 0.6rem;
    vertical-align: middle;
    border-bottom: 1px solid #f0eef5;

    ::v-deep .form-control,
    ::v-deep .vs__dropdown-toggle {
      min-height: 42px;
      border-color: #dedbe8;
      border-radius: 7px;
      background: #fff;
    }

    ::v-deep .form-control:focus,
    ::v-deep .v-select.vs--open .vs__dropdown-toggle {
      border-color: #8b80f2;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.09);
    }
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
}

.crest-qty-column {
  width: 80px !important;
  text-align: center;
}

.crest-action-column {
  width: 48px !important;
}

.crest-stationary-table {
  min-width: 560px;

  th:first-child {
    width: auto;
  }
}

.crest-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2.25rem;
  color: #aaa6b8;
  border: 1px dashed #cbc7dd;
  border-radius: 10px;
  background: #fbfaff;

  span {
    font-weight: 500;
  }
}

.crest-label {
  display: block;
  color: #82868b;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
}

.crest-value {
  margin-top: 0.3rem;
  color: #3f4254;
  font-weight: 600;
}

.crest-form-footer {
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  border-top: 1px solid #ebe9f1;

  .btn {
    min-width: 120px;
    min-height: 42px;
    border-radius: 8px;
    font-weight: 600;
  }
}

.crest-section {
  ::v-deep .form-group label {
    color: #555169;
    font-size: 0.8rem;
    font-weight: 600;
  }

  ::v-deep .form-control,
  ::v-deep .vs__dropdown-toggle {
    min-height: 43px;
    border-color: #dedbe8;
    border-radius: 8px;
  }

  ::v-deep .vs__selected,
  ::v-deep .vs__search {
    padding-left: 0.35rem;
    font-size: 0.9rem;
  }
}

@media (max-width: 767.98px) {
  .crest-book-list-form .crest-form-card ::v-deep .card-body {
    padding: 0.9rem;
  }

  .crest-page-header {
    padding: 0.9rem;
  }

  .crest-page-header__icon {
    display: none;
  }

  .crest-page-title {
    font-size: 1.15rem;
  }

  .crest-section {
    padding: 1rem;
  }

  .crest-section-heading,
  .crest-subject-adder {
    align-items: stretch;
    flex-direction: column;
  }

  .crest-subject-adder {
    width: 100%;
  }

  .crest-subject-select {
    width: 100%;
    margin-bottom: 0.5rem;
  }

  .crest-subject-adder .btn {
    margin-left: 0 !important;
  }

  .crest-subject-card {
    margin-left: -0.35rem;
    margin-right: -0.35rem;
  }

  .crest-table-wrap {
    padding: 0.5rem;
  }

  .crest-form-footer {
    gap: 0.75rem;

    .btn {
      flex: 1;
      min-width: 0;
    }
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
